/**
 * StampSection - 교육과정 관리 섹션
 *
 * 교육과정 목록 탭과 새 교육과정 추가 탭을 포함
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../common';
import { BookOpen, Plus } from 'lucide-react';
import StampCard from './StampCard';

function StampSection({
  studentId,
  stamps,
  problemsByTree,
  extractedCount,
  activeTab,
  onTabChange,
  onAITestClick,
  onStampsChange
}) {
  const { getAccessToken } = useAuth();
  const [treeList, setTreeList] = useState([]);
  const [selectedTreeId, setSelectedTreeId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  // 교육과정 목록 조회
  useEffect(() => {
    if (activeTab === 'create') {
      fetchTreeList();
    }
  }, [activeTab]);

  const fetchTreeList = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/testset/lists`,
        {
          headers: { 'Authorization': token }
        }
      );
      const data = await response.json();
      if (data.status === 'success') {
        setTreeList(data.data || []);
      }
    } catch (error) {
      console.error('교육과정 목록 조회 실패:', error);
    }
  };

  // 스탬프 생성
  const handleCreateStamp = async () => {
    if (!selectedTreeId || !studentId) return;

    setIsCreating(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            tree_id: selectedTreeId,
            student_id: parseInt(studentId)
          })
        }
      );

      if (response.status === 409) {
        alert('이미 존재하는 스탬프입니다.');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      onStampsChange?.();
      onTabChange('list');
      setSelectedTreeId(null);
    } catch (error) {
      console.error('스탬프 생성 오류:', error);
      alert('스탬프 생성에 실패했습니다.');
    } finally {
      setIsCreating(false);
    }
  };

  // 스탬프 삭제
  const handleDeleteStamp = async (stampId, examCount) => {
    if (examCount > 0) {
      alert('시험이 존재하는 스탬프는 삭제할 수 없습니다.');
      return;
    }

    if (!window.confirm('스탬프를 삭제하시겠습니까?')) return;

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ stamp_id: stampId })
        }
      );

      if (response.status === 409) {
        alert('시험이 존재하는 스탬프는 삭제할 수 없습니다.');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      onStampsChange?.();
    } catch (error) {
      console.error('스탬프 삭제 오류:', error);
      alert('스탬프 삭제에 실패했습니다.');
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
      {/* 탭 메뉴 */}
      <div className="flex items-center border-b mb-4">
        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'list'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => onTabChange('list')}
        >
          교육과정 목록
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'create'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => onTabChange('create')}
        >
          새 교육과정 추가
        </button>
        {extractedCount > 0 && (
          <span className="ml-auto text-sm font-medium text-primary">
            AI 추출 문제: {extractedCount}개
          </span>
        )}
      </div>

      {/* 탭 컨텐츠 */}
      {activeTab === 'list' ? (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))' }}>
          {stamps.length > 0 ? (
            stamps.map((stamp) => (
              <StampCard
                key={stamp.stamp_id}
                stamp={stamp}
                studentId={studentId}
                problemCount={problemsByTree[String(stamp.tree_id)]?.length || 0}
                onAITestClick={onAITestClick}
                onDelete={handleDeleteStamp}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>등록된 교육과정이 없습니다.</p>
              <p className="text-sm mt-1">상단의 '새 교육과정 추가' 탭을 클릭하여 추가하세요.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              교육과정 선택
            </label>
            <select
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
              onChange={(e) => setSelectedTreeId(parseInt(e.target.value))}
              value={selectedTreeId || ''}
            >
              <option value="">교육과정을 선택하세요</option>
              {treeList
                .filter(tree => tree.is_closed)
                .map((tree) => (
                  <option key={tree.tree_id} value={tree.tree_id}>
                    {tree.tree_name} ({tree.edu_ver})
                  </option>
                ))}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                onTabChange('list');
                setSelectedTreeId(null);
              }}
            >
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateStamp}
              disabled={!selectedTreeId || isCreating}
              className="flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              {isCreating ? '생성 중...' : '생성'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StampSection;
